import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { LanguageContext } from "./LanguageContext";

const Menu = ({ handlePrevClick, handleNextClick, clickDisabled }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [audioContext, setAudioContext] = useState(null);
  const { language, toggleLanguage } = useContext(LanguageContext);
  const [isLanguageButtonEnabled, setIsLanguageButtonEnabled] = useState(true);
  const [isSoundEnabled, setIsSoundEnabled] = useState(true);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 921);
  const [showSoundMutedIndicator, setShowSoundMutedIndicator] = useState(false);
  const backgroundAudioRef = useRef(null);

  const cameraSwitchSounds = [
    "https://sybf.fr/wp-content/themes/sybf/src/assets/sounds/switch_cam1.mp3",
    "https://sybf.fr/wp-content/themes/sybf/src/assets/sounds/switch_cam2.mp3",
    "https://sybf.fr/wp-content/themes/sybf/src/assets/sounds/switch_cam3.mp3",
    "https://sybf.fr/wp-content/themes/sybf/src/assets/sounds/switch_cam4.mp3",
  ];

  const languageSwitchSounds = [
    "https://sybf.fr/wp-content/themes/sybf/src/assets/sounds/switch_language1.mp3",
    "https://sybf.fr/wp-content/themes/sybf/src/assets/sounds/switch_language2.mp3",
    "https://sybf.fr/wp-content/themes/sybf/src/assets/sounds/switch_language3.mp3",
  ];

  useEffect(() => {
    const context = new (window.AudioContext || window.webkitAudioContext)();
    setAudioContext(context);

    const items = document.querySelectorAll("#menu-list li");
    items.forEach((item) => {
      item.addEventListener("mouseover", () => {
        items.forEach((i) => i.classList.remove("hovered"));
        item.classList.add("hovered");
      });
    });
    items[0].classList.add("hovered");
    setMenuItems(items);

    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 921);
    };

    window.addEventListener("resize", handleResize);

    // Initial check for window size
    handleResize();

    return () => {
      items.forEach((item) => {
        item.removeEventListener("mouseover", () => {});
      });
      context.close();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setShowSoundMutedIndicator(!isSoundEnabled && !isSmallScreen);
    if (backgroundAudioRef.current) {
      backgroundAudioRef.current.muted = !isSoundEnabled;
    }
  }, [isSoundEnabled, isSmallScreen]);

  const playSound = useCallback(
    (soundsArray) => {
      if (audioContext && isSoundEnabled) {
        const randomUrl =
          soundsArray[Math.floor(Math.random() * soundsArray.length)];
        fetch(randomUrl)
          .then((response) => response.arrayBuffer())
          .then((arrayBuffer) => audioContext.decodeAudioData(arrayBuffer))
          .then((buffer) => {
            const source = audioContext.createBufferSource();
            source.buffer = buffer;
            source.connect(audioContext.destination);
            source.start(0);
          });
      }
    },
    [audioContext, isSoundEnabled]
  );

  const handleLanguageButtonClick = useCallback(() => {
    if (isLanguageButtonEnabled) {
      toggleLanguage();
      playSound(languageSwitchSounds);

      const vbTextElements = document.querySelectorAll(".vb-text");
      vbTextElements.forEach((el) => {
        el.classList.remove("clicked-animation");
        void el.offsetWidth;
        el.classList.add("clicked-animation");
      });

      setIsLanguageButtonEnabled(false);
      setTimeout(() => {
        setIsLanguageButtonEnabled(true);
        vbTextElements.forEach((el) => {
          el.classList.remove("clicked-animation");
        });
      }, 800);
    }
  }, [isLanguageButtonEnabled, playSound, toggleLanguage]);

  const handlePrev = useCallback(() => {
    if (!clickDisabled) {
      handlePrevClick();
      playSound(cameraSwitchSounds);
    }
  }, [clickDisabled, handlePrevClick, playSound]);

  const handleNext = useCallback(() => {
    if (!clickDisabled) {
      handleNextClick();
      playSound(cameraSwitchSounds);
    }
  }, [clickDisabled, handleNextClick, playSound]);

  const handleSoundButtonClick = useCallback(() => {
    setIsSoundEnabled((prev) => !prev);
  }, []);

  return (
    <div className="menu-camera-selector vibrate-container">
      <audio
        ref={backgroundAudioRef}
        src="https://sybf.fr/wp-content/themes/sybf/src/assets/sounds/ambience/room-buzz.mp3"
        loop
        autoPlay
      />
      <ul id="menu-list">
        <h1 className="vb-text">{language === "en" ? "MENU" : "MENU"}</h1>

        <MenuItem
          handleClick={handlePrev}
          label={language === "en" ? "PREV CAMERA" : "CAMERA PREC"}
          isSmallScreen={isSmallScreen}
          iconSrc="https://sybf.fr/wp-content/themes/sybf/src/assets/img/prev-camera.svg"
        />
        <MenuItem
          handleClick={handleNext}
          label={language === "en" ? "NEXT CAMERA" : "CAMERA SUIV"}
          isSmallScreen={isSmallScreen}
          iconSrc="https://sybf.fr/wp-content/themes/sybf/src/assets/img/next-camera.svg"
        />
        <li data-index="2" className="vb-text">
          <a onClick={handleSoundButtonClick}>
            <span>{language === "en" ? "SOUND" : "SON"}</span>
            {isSmallScreen ? (
              <>
                <img
                  src="https://sybf.fr/wp-content/themes/sybf/src/assets/img/sound-on.svg"
                  alt="sound_on"
                  className="sound_on icon-menu"
                  style={{ display: isSoundEnabled ? "block" : "none" }}
                />
                <img
                  src="https://sybf.fr/wp-content/themes/sybf/src/assets/img/sound-off.svg"
                  alt="sound_off"
                  className="sound_off icon-menu"
                  style={{ display: isSoundEnabled ? "none" : "block" }}
                />
              </>
            ) : showSoundMutedIndicator ? (
              <span
                className="sound-muted-indicator"
                style={{ marginLeft: "10px" }}
              >
                {language === "en" ? "OFF" : "DESACTIVE"}
              </span>
            ) : null}
          </a>
        </li>
        <li data-index="1" className="vb-text active">
          <a
            onClick={handleLanguageButtonClick}
            style={{ pointerEvents: isLanguageButtonEnabled ? "auto" : "none" }}
          >
            <span>{language === "en" ? "LANGUAGE FR" : "LANGUE EN"}</span>
            {isSmallScreen && (
              <>
                <img
                  src="https://sybf.fr/wp-content/themes/sybf/src/assets/img/flag-fr.svg"
                  alt="flag_fr"
                  className="flag_fr icon-menu"
                  style={{ display: language === "en" ? "block" : "none" }}
                />
                <img
                  src="https://sybf.fr/wp-content/themes/sybf/src/assets/img/flag-en.svg"
                  alt="flag_en"
                  className="flag_en icon-menu"
                  style={{ display: language === "en" ? "none" : "block" }}
                />
              </>
            )}
          </a>
        </li>
      </ul>
    </div>
  );
};

const MenuItem = ({ handleClick, label, isSmallScreen, iconSrc }) => (
  <li className="vb-text">
    <a onClick={handleClick}>
      <span>{label}</span>
      {isSmallScreen && <img src={iconSrc} alt="icon" className="icon-menu" />}
    </a>
  </li>
);

export default Menu;
